import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Polygon, Marker, Popup, useMap, useMapEvents, LayersControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Address and Props Types
interface Address {
  id?: string;
  latitude: number;
  longitude: number;
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  pinCode?: string;
}

interface PolygonMapComponentProps {
  addresses?: Address[];
  boundaryData?: { latitude: number; longitude: number }[]; // For boundary data
}

const PolygonMapComponent: React.FC<PolygonMapComponentProps> = ({ addresses = [], boundaryData = [] }) => {
  const [marker, setMarker] = useState<L.LatLng | null>(null); // State to track a single marker

  const defaultCenter: [number, number] =
    addresses.length > 0
      ? [addresses[0].latitude, addresses[0].longitude]
      : [9.9921, 76.3019]; // Default to a location if no addresses

  const polygonPositions = addresses.map(address =>
    L.latLng(address.latitude, address.longitude)
  );
  const boundaryPositions = boundaryData.map(boundary =>
    L.latLng(boundary.latitude, boundary.longitude)
  );

  // Event handler to add marker on click
  const MapClickHandler = () => {
    useMapEvents({
      click(e) {
        const { lat, lng } = e.latlng;
        setMarker(L.latLng(lat, lng)); // Update the state with the new marker (replaces the previous one)
      }
    });
    return null; // The component does not render anything
  };

  const handleMarkerClick = (lat: number, lng: number) => {
    const googleMapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
    window.open(googleMapsUrl, '_blank');
  };

  const FitBoundsHandler = () => {
    const map = useMap();

    useEffect(() => {
      if (polygonPositions.length > 0) {
        const bounds = L.latLngBounds(polygonPositions);
        map.fitBounds(bounds); // Adjust the map view to fit the polygon
      }
    }, [polygonPositions, map]);

    return null;
  };

  return (
    <div style={{ height: '535px', width: '100%' }}>
      <MapContainer center={defaultCenter} zoom={13} className="h-full w-full">
        <LayersControl position="topright">
        <LayersControl.BaseLayer name="Default Map" checked>

        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
         </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Satellite">
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution='&copy; Esri, Maxar, Earthstar Geographics, and the GIS User Community'
            />
          </LayersControl.BaseLayer>
        </LayersControl>

        {polygonPositions.length > 2 && (
          <Polygon positions={polygonPositions} color="blue" opacity={0.5} />
        )}

        {boundaryPositions.length > 0 && (
          <Polygon positions={boundaryPositions} color="red" opacity={0.5} />
        )}

        <MapClickHandler />
        <FitBoundsHandler />

        {marker && (
          <Marker position={marker} eventHandlers={{ click: () => handleMarkerClick(marker.lat, marker.lng) }}>
            <Popup>
              <span>Click to open in Google Maps</span>
            </Popup>
          </Marker>
        )}
      </MapContainer>
    </div>
  );
};

export default PolygonMapComponent;
